import React, { useState } from 'react';
import { Table, Tag, Row, Col, Alert, Spin, Space, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { createUseStyles } from 'react-jss';
import { useSearchParams } from 'react-router-dom-v5-compat';
import type { SimulationRawResultsV2Summary, SimulationRawResultsV2ListParams } from '~/store/simulation/types';
import type { ShipiumTagConfig } from '~/components/shared/ShipiumTag';
import { ShipiumTagWithConfig } from '~/components/shared/ShipiumTag';
import StyledCodeMirror from '~/components/shared/StyledCodeMirror';
import {
  useSimulationRawResultsList,
  useSimulationRawResultDetail,
} from '~/hooks/simulation/useSimulationRawResultsV2';
import { useCarrierServiceMethods } from '~/hooks/queries/useCarriersFiltered';

const useStyles = createUseStyles({
  tableStyles: {
    '&& table > .ant-table-thead > tr > th': { fontWeight: 'bold' },
    '&& table > .ant-table-tbody > tr > td': { fontFamily: 'monospace', fontSize: '0.9em' },
  },
  stripedRow: {
    '&& > td': { background: '#fafafa' },
  },
  detailPanes: {
    background: '#f0f7ff',
    padding: 12,
    contain: 'inline-size',
    '& .cm-editor': { maxWidth: '100% !important' },
  },
});

const { Text } = Typography;

const statusTagMap: Record<string, ShipiumTagConfig> = {
  skipped: { color: 'Fuschia', label: 'Skipped', dark: true, icon: 'warning' },
  error: { color: 'Coral', label: 'Error', dark: true, icon: 'exclamation' },
  success: { color: 'Teal', label: 'Success', dark: true, icon: 'check' },
};

export type StatusFilter = 'success' | 'error' | 'skipped' | 'all';
export type SortBy = 'shippedDateTime' | 'status' | 'duration';
export type SortDir = 'asc' | 'desc';

const STATUS_VALUES: StatusFilter[] = ['success', 'error', 'skipped', 'all'];
// Validate the status URL param down to the known enum (shared by the tab's stat-card highlight and
// the table's list query) so a tampered ?status=garbage falls back consistently to 'all'.
export const parseStatus = (raw: string | null): StatusFilter =>
  STATUS_VALUES.includes(raw as StatusFilter) ? (raw as StatusFilter) : 'all';

// Same defense for the sort params: a truthy-but-invalid ?sortBy=garbage would otherwise slip past a
// bare `|| default` and reach the API, so validate down to the known enums before sending.
const SORT_BY_VALUES: SortBy[] = ['shippedDateTime', 'status', 'duration'];
const SORT_DIR_VALUES: SortDir[] = ['asc', 'desc'];
const parseSortBy = (raw: string | null): SortBy =>
  SORT_BY_VALUES.includes(raw as SortBy) ? (raw as SortBy) : 'shippedDateTime';
const parseSortDir = (raw: string | null): SortDir =>
  SORT_DIR_VALUES.includes(raw as SortDir) ? (raw as SortDir) : 'desc';

export interface SimulationRawResultsTableV2Props {
  simulationId: string;
  isProcessing?: boolean;
}

// while the sim is in-flight, poll the list so newly-processed shipments stream in; the tab reuses
// this same interval for its detail poll, so it's exported rather than redefined there.
export const PROCESSING_POLL_MS = 15000;

// Renders the opaque rawResult bag. Pulls request/response/error out if present; falls back to the whole blob.
const DetailPanes = ({ simulationId, shipment }: { simulationId: string; shipment: SimulationRawResultsV2Summary }) => {
  const classes = useStyles();
  const { data, isLoading, isError } = useSimulationRawResultDetail(simulationId, shipment.historicalShipmentId);
  if (isLoading)
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  if (isError || !data) return <Alert type="error" message="Failed to load shipment detail." showIcon />;
  const request = 'request' in data ? data.request : data;
  const response = 'response' in data ? data.response : undefined;
  const error = 'error' in data ? String(data.error ?? '') : '';
  const referenceIdentifier = 'referenceIdentifier' in data ? String(data.referenceIdentifier ?? '') : '';
  return (
    <div className={classes.detailPanes}>
      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 8 }} />}
      <Space size="large" style={{ marginBottom: 8 }}>
        <Text strong>
          Historical Shipment Id: <Text code>{shipment.historicalShipmentId}</Text>
        </Text>
        <Text strong>
          Reference Identifier: <Text code>{referenceIdentifier || '—'}</Text>
        </Text>
      </Space>
      <Row gutter={[16, 16]}>
        <Col span={response ? 12 : 24} style={{ minWidth: 0 }}>
          <StyledCodeMirror
            title="Request"
            initialValue={JSON.stringify(request ?? {}, null, 2)}
            readOnly
            mode="javascript"
            full
            contained
          />
        </Col>
        {!!response && (
          <Col span={12} style={{ minWidth: 0 }}>
            <StyledCodeMirror
              title="Response"
              initialValue={JSON.stringify(response, null, 2)}
              readOnly
              mode="javascript"
              full
              contained
            />
          </Col>
        )}
      </Row>
    </div>
  );
};

export const SimulationRawResultsTableV2 = ({ simulationId, isProcessing }: SimulationRawResultsTableV2Props) => {
  const classes = useStyles();
  const carrierServiceMethodMap = useCarrierServiceMethods();
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [query, setQuery] = useSearchParams();

  const status = parseStatus(query.get('status'));
  const sortBy = parseSortBy(query.get('sortBy'));
  const sortDir = parseSortDir(query.get('sortDir'));
  // Clamp/validate read from the URL — an untrusted or absent param (null → Number('')===0,
  // ?page=abc, ?pageSize=99999, ?page=0) must never reach limit/offset as NaN/negative/over-cap,
  // and pageSize must stay in the 25..100 range we offer (so "1 / page" can never appear).
  const rawSizeStr = query.get('pageSize');
  const parsedSize = rawSizeStr === null ? NaN : Number(rawSizeStr);
  const pageSize =
    Number.isFinite(parsedSize) && parsedSize >= 1 ? Math.min(Math.max(Math.floor(parsedSize), 25), 100) : 50;
  const rawPage = Number(query.get('page'));
  const page = Number.isFinite(rawPage) && rawPage >= 1 ? Math.floor(rawPage) : 1;

  const setParams = (next: Record<string, string | number | undefined>) => {
    Object.entries(next).forEach(([k, v]) => {
      if (v === undefined || v === '') query.delete(k);
      else query.set(k, String(v));
    });
    setQuery(query);
  };

  const { data, isLoading, isError } = useSimulationRawResultsList(
    {
      simulationId,
      status: status === 'all' ? undefined : status,
      sortBy,
      sortDir,
      limit: pageSize,
      offset: (page - 1) * pageSize,
      carrier: query.get('carrier') || undefined,
      tntType: query.get('tntType') || undefined,
      dayOfWeek: (query.get('dayOfWeek') || undefined) as SimulationRawResultsV2ListParams['dayOfWeek'],
      tenantId: query.get('tenantId') || undefined,
      originSlug: query.get('originSlug') || undefined,
    },
    isProcessing ? PROCESSING_POLL_MS : false,
  );

  const rows = data?.simulationShipments ?? [];

  const columns: ColumnsType<SimulationRawResultsV2Summary> = [
    {
      title: 'Carrier Service Method',
      key: 'csm',
      align: 'left',
      render: (r: SimulationRawResultsV2Summary) =>
        (r.carrierDecisionCarrierServiceMethodId &&
          carrierServiceMethodMap[r.carrierDecisionCarrierServiceMethodId]?.name) ||
        r.carrierDecisionCarrierServiceMethodId ||
        '—',
    },
    {
      title: 'Shipped',
      key: 'shippedDateTime',
      sorter: true,
      sortOrder: sortBy === 'shippedDateTime' ? (sortDir === 'asc' ? 'ascend' : 'descend') : undefined,
      render: (r) => (r.shippedDateTime ? new Date(r.shippedDateTime).toLocaleString() : '—'),
    },
    {
      title: 'Origin',
      key: 'origin',
      render: (r) => `${r.fromPostalCode ?? ''} ${r.fromState ?? ''} ${r.fromCountryCode ?? ''}`.trim(),
    },
    {
      title: 'Destination',
      key: 'destination',
      render: (r) =>
        `${r.destinationPostalCode ?? ''} ${r.destinationState ?? ''} ${r.destinationCountryCode ?? ''}`.trim(),
    },
    {
      title: 'Duration (ms)',
      key: 'duration',
      align: 'right',
      sorter: true,
      sortOrder: sortBy === 'duration' ? (sortDir === 'asc' ? 'ascend' : 'descend') : undefined,
      render: (r) => r.duration ?? '—',
    },
    {
      // Status is filtered via the stat-card buttons above, not column sorting.
      title: 'Status',
      key: 'status',
      align: 'center',
      render: (r) => (r.status ? <ShipiumTagWithConfig configMap={statusTagMap} tag={r.status} /> : <Tag>unknown</Tag>),
    },
  ];

  if (isError) return <Alert type="error" message="Failed to load shipments." showIcon />;

  return (
    <Table<SimulationRawResultsV2Summary>
      loading={isLoading}
      size="small"
      rowKey={(r) => r.historicalShipmentId}
      rowClassName={(_r, index) => (index % 2 === 1 ? classes.stripedRow : '')}
      columns={columns}
      dataSource={rows}
      className={classes.tableStyles}
      expandable={{
        // multiple rows may be expanded at once; skipped shipments weren't simulated (no
        // request/response to show), so they aren't expandable
        rowExpandable: (r) => r.status !== 'skipped',
        expandedRowKeys: expandedKeys,
        onExpand: (expanded, r) =>
          setExpandedKeys((keys) =>
            expanded ? [...keys, r.historicalShipmentId] : keys.filter((k) => k !== r.historicalShipmentId),
          ),
        expandedRowRender: (r) => <DetailPanes simulationId={simulationId} shipment={r} />,
      }}
      pagination={{
        pageSize,
        total: data?.options.count ?? 0,
        current: page,
        showSizeChanger: true,
        showTotal: (total: number) => `${total.toLocaleString()} shipments`,
        pageSizeOptions: ['25', '50', '100'],
        position: ['bottomRight'],
        style: { marginRight: 16 },
      }}
      onChange={(pagination, _filters, sorter) => {
        const s = Array.isArray(sorter) ? sorter[0] : sorter;
        const nextPageSize = Math.min(pagination.pageSize ?? pageSize, 100);
        const pageSizeChanged = nextPageSize !== pageSize;
        // Sorting is a 2-state asc/desc toggle: the server always sorts by something, so there is
        // no meaningful "no sort" state. antd's 3rd click reports order===undefined — instead of
        // clearing (which strands the default column on descend, unable to reach ascend), flip the
        // clicked column's direction. columnKey may be dropped on that cancel click → fall back to
        // the current sort column.
        const clicked = (s?.columnKey as SortBy) ?? sortBy;
        const nextSortDir: SortDir = s?.order
          ? s.order === 'ascend'
            ? 'asc'
            : 'desc'
          : clicked === sortBy && sortDir === 'desc'
            ? 'asc'
            : 'desc';
        setParams({
          page: pageSizeChanged ? 1 : (pagination.current ?? 1),
          pageSize: nextPageSize,
          sortBy: clicked,
          sortDir: nextSortDir,
        });
      }}
    />
  );
};

export default SimulationRawResultsTableV2;
